import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { ComicHeading, ComicPanel, ComicButton } from '../components/ComicPanel';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.detail || result.message || 'Failed to send message.');
      }

      toast.success("Message sent! We'll get back to you soon.");
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      toast.error(error.message || 'Could not send your message right now. Please try again later.');
      console.error('Contact form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Hero */}
        <div className="text-center mb-16">
          <div
            className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border-2 border-[#2FB4E9]"
            style={{ background: '#2FB4E9' }}
          >
            <MessageSquare className="w-8 h-8 text-white" />
          </div>

          <ComicHeading as="h1" className="text-4xl sm:text-5xl mb-6" data-testid="contact-title">
            Contact Us
          </ComicHeading>

          <p className="text-slate-300 text-lg">
            Got questions, feedback, or just want to say hi? We&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Info */}
          <div>
            <ComicPanel className="p-6 mb-6">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 flex items-center justify-center border-2 border-[#00FFFF]"
                  style={{
                    background: 'linear-gradient(90deg, #00FFFF 0%, #00E5E5 50%, #00BFCF 100%)',
                  }}
                >
                  <Mail className="w-6 h-6 text-[#041014]" />
                </div>

                <div>
                  <h3 className="text-white font-bold">Email Us</h3>
                  <a
                    href="mailto:editorial@meltedportstudios.com"
                    className="text-[#00E5E5] hover:text-[#2FB4E9]"
                  >
                    editorial@meltedportstudios.com
                  </a>
                </div>
              </div>
            </ComicPanel>

            <ComicPanel className="p-6">
              <h3 className="text-[#2FB4E9] uppercase font-bold tracking-widest mb-4">
                Response Time
              </h3>
              <p className="text-slate-300">
                We typically respond within 1-2 business days. For urgent matters, please include
                &quot;URGENT&quot; in your subject line.
              </p>
            </ComicPanel>
          </div>

          {/* Contact Form */}
          <ComicPanel className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label
                  htmlFor="name"
                  className="text-[#2FB4E9] uppercase font-bold tracking-widest text-sm"
                >
                  Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-2 bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-[#2FB4E9] rounded-none"
                  placeholder="Your name"
                  data-testid="contact-name"
                />
              </div>

              <div>
                <Label
                  htmlFor="email"
                  className="text-[#2FB4E9] uppercase font-bold tracking-widest text-sm"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-2 bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-[#2FB4E9] rounded-none"
                  placeholder="you@example.com"
                  data-testid="contact-email"
                />
              </div>

              <div>
                <Label
                  htmlFor="subject"
                  className="text-[#2FB4E9] uppercase font-bold tracking-widest text-sm"
                >
                  Subject
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="mt-2 bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-[#2FB4E9] rounded-none"
                  placeholder="What&apos;s this about?"
                  data-testid="contact-subject"
                />
              </div>

              <div>
                <Label
                  htmlFor="message"
                  className="text-[#2FB4E9] uppercase font-bold tracking-widest text-sm"
                >
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="mt-2 bg-slate-900 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-[#2FB4E9] rounded-none resize-none"
                  placeholder="Your message..."
                  data-testid="contact-message"
                />
              </div>

              <ComicButton
                type="submit"
                variant="primary"
                className="w-full"
                disabled={loading}
                data-testid="contact-submit"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </ComicButton>
            </form>
          </ComicPanel>
        </div>
      </div>
    </Layout>
  );
};

export default Contact;
